import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Gift, Heart, Sparkles, CheckCircle2, Download, ArrowRight, Cake, CalendarHeart } from "lucide-react";
import { API_URL, BRAND, resolveAsset } from "../lib/brand";

const OCCASION_LABELS = {
  compleanno: "🎂 Compleanno",
  anniversario: "💗 Anniversario",
  natale: "🎄 Natale",
  "festa-mamma": "👩 Festa della Mamma",
  "festa-papa": "👨 Festa del Papà",
  battesimo: "👶 Battesimo o Nascita",
  generico: "✨ Un pensiero speciale",
};

const PAYMENT_LABELS = {
  bonifico: "🏦 Bonifico bancario",
  contanti: "💵 Contanti",
  paypal: "📲 PayPal / Satispay",
  whatsapp: "💬 Concordiamo su WhatsApp",
};

export default function RegalaCura() {
  const [services, setServices] = useState([]);
  const [occasions, setOccasions] = useState([]);
  const [form, setForm] = useState({
    buyer_name: "",
    buyer_email: "",
    buyer_phone: "",
    service_type: "",
    amount: 0,
    occasion: "generico",
    recipient_name: "",
    recipient_email: "",
    personal_message: "",
    delivery: "digital",
    payment_preference: "bonifico",
    notes: "",
    discount_code: "",
    consent: false,
  });
  const [discountStatus, setDiscountStatus] = useState(null); // {valid, discount_pct} | null
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    document.title = "Regala Cura · Voucher per ogni occasione · Teresa Vilardo";
    axios.get(`${API_URL}/voucher/services`).then((r) => {
      setServices(r.data.services || []);
      setOccasions(r.data.occasions || []);
    }).catch(() => {});
  }, []);

  const currentService = useMemo(() => services.find((s) => s.key === form.service_type), [services, form.service_type]);

  const pickService = (svc) => {
    setForm((prev) => ({ ...prev, service_type: svc.key }));
    const el = document.getElementById("voucher-form");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const validateDiscount = async () => {
    const code = form.discount_code.trim().toUpperCase();
    if (!code) { setDiscountStatus(null); return; }
    try {
      const r = await axios.post(`${API_URL}/voucher/validate-discount`, { code });
      if (r.data.valid) {
        setDiscountStatus({ valid: true, discount_pct: r.data.discount_pct });
        toast.success(`Codice amico valido — sconto ${r.data.discount_pct}% applicato`);
      } else {
        setDiscountStatus({ valid: false });
        toast.error("Codice amico non valido o già usato");
      }
    } catch {
      setDiscountStatus({ valid: false });
    }
  };

  const canSubmit = () =>
    form.buyer_name.trim() &&
    form.buyer_email.trim() &&
    form.service_type &&
    form.recipient_name.trim() &&
    form.consent;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit()) { toast.error("Compila i campi obbligatori"); return; }
    setSending(true);
    try {
      const r = await axios.post(`${API_URL}/voucher/request`, form);
      setSuccess(r.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore invio richiesta");
    } finally { setSending(false); }
  };

  const OCCASION_ICON = { compleanno: Cake, anniversario: Heart, natale: Gift, "festa-mamma": Heart, "festa-papa": Heart, battesimo: Sparkles, generico: Sparkles };

  return (
    <div className="min-h-screen bg-[#FDECEF]" data-testid="regala-cura-page">
      {/* HERO */}
      <section className="relative overflow-hidden px-6 md:px-10 pt-16 md:pt-24 pb-16 md:pb-20">
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-[#FF2768]/8 blur-3xl" />
        <div className="absolute -bottom-32 -left-16 w-96 h-96 rounded-full bg-[#81C3C9]/12 blur-3xl" />

        <div className="max-w-6xl mx-auto relative">
          <Link to="/" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.35em] text-[#FF2768] hover:underline mb-8" data-testid="link-home">
            ← Torna al sito
          </Link>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#FF2768]/30 mb-6">
              <Gift size={14} className="text-[#FF2768]" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#FF2768] font-bold">Voucher di Cura · Un regalo di tempo e ascolto</span>
            </div>

            <h1 className="font-display italic text-5xl md:text-7xl lg:text-8xl text-[#3D2140] leading-[0.9] max-w-4xl">
              Voucher di <span className="text-[#FF2768]">Cura</span>
            </h1>
            <p className="mt-4 font-editorial italic text-2xl md:text-3xl text-[#FF2768]">
              Regala tempo, ascolto e cura.
            </p>
            <p className="mt-6 text-lg md:text-xl text-[#3D2140]/75 max-w-2xl leading-relaxed">
              Ci sono regali che non si incartano. Un momento di ascolto, un accompagnamento educativo, uno spazio per ritrovare fiducia, tempo e serenità.
            </p>
            <p className="mt-4 text-base text-[#4E9EA5] italic max-w-2xl">
              Con i Voucher di Cura puoi scegliere di regalare un'esperienza personalizzata, pensata per accompagnare famiglie, genitori e professioniste dell'infanzia.
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <a href="#servizi" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF2768] text-white text-sm font-semibold hover:bg-[#e91e5e]" data-testid="cta-scroll-services">
                Scegli il voucher <ArrowRight size={14} />
              </a>
              <a href="#come-funziona" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border border-[#3D2140]/15 text-[#3D2140] text-sm font-semibold hover:border-[#FF2768]">
                Come funziona
              </a>
            </div>

            {/* Occasion chips */}
            <div className="mt-10 flex flex-wrap gap-2">
              {Object.entries(OCCASION_LABELS).map(([k, label]) => (
                <span key={k} className="px-3 py-1.5 rounded-full bg-white/70 border border-[#3D2140]/10 text-xs text-[#3D2140]/80">{label}</span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* SUCCESS BANNER */}
      {success && (
        <section className="px-6 md:px-10 pb-12">
          <div className="max-w-3xl mx-auto bg-white rounded-3xl p-8 md:p-10 border border-[#FF2768]/20 shadow-xl" data-testid="voucher-success">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-full bg-[#FF2768] text-white flex items-center justify-center flex-shrink-0">
                <CheckCircle2 size={24} />
              </div>
              <div className="flex-1">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF2768] font-bold">Richiesta ricevuta</p>
                <h3 className="font-display italic text-3xl text-[#3D2140] mt-2">Grazie, il tuo voucher è pronto.</h3>
                <p className="mt-3 text-[#3D2140]/75 leading-relaxed">
                  Ti ho appena mandato una email con il PDF del voucher e il codice <code className="bg-[#FFE45C] px-2 py-0.5 rounded font-mono text-sm">{success.code}</code>. Ti contatterò a breve per comunicarti il prezzo, il pagamento e organizzare la consegna.
                </p>
                {success.pdf_url && (
                  <a href={resolveAsset(success.pdf_url)} target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#3D2140] text-white text-sm font-semibold hover:bg-[#164244]" data-testid="btn-download-pdf">
                    <Download size={14} /> Scarica il PDF del voucher
                  </a>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* SERVIZI */}
      <section id="servizi" className="px-6 md:px-10 py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] font-bold text-center">— Scegli il voucher</p>
          <h2 className="mt-3 font-display italic text-4xl md:text-5xl text-[#3D2140] text-center">Cinque modi per regalare cura</h2>
          <p className="mt-4 text-center text-[#3D2140]/70 max-w-2xl mx-auto">Chiedimi un consiglio su WhatsApp oppure crea subito il tuo voucher online. Il prezzo te lo comunico io in privato — così parliamone insieme.</p>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((svc) => {
              const editorialTitle = {
                "consulenza-singola": "Una consulenza per te",
                "pacchetto-3-consulenze": "Un percorso da condividere",
                "visita-domicilio": "Cura a domicilio",
                "formazione-tate": "Crescere nella professione",
                "voucher-valore": "Un regalo su misura",
              }[svc.key] || svc.label;
              const wa = `https://wa.me/393403993522?text=${encodeURIComponent(`Buongiorno Teresa, vorrei ricevere informazioni sul Voucher di Cura “${editorialTitle}”.`)}`;
              const selected = form.service_type === svc.key;
              return (
                <div
                  key={svc.key}
                  className={`p-7 rounded-3xl border transition-all ${selected ? "border-[#FF2768] bg-[#FFF5F7] shadow-lg" : "border-[#3D2140]/10 bg-white hover:border-[#FF2768]/40 hover:shadow-md"}`}
                  data-testid={`voucher-service-${svc.key}`}
                >
                  <div className="w-10 h-10 rounded-full bg-[#FFE45C] flex items-center justify-center mb-4">
                    <Heart size={16} className="text-[#FF2768]" />
                  </div>
                  <h3 className="font-display text-xl text-[#3D2140] leading-tight">{editorialTitle}</h3>
                  <p className="mt-3 text-sm text-[#3D2140]/65 leading-relaxed">
                    {svc.key === "consulenza-singola" && "Consulenza individuale online della durata di 60 minuti."}
                    {svc.key === "pacchetto-3-consulenze" && "Pacchetto di tre consulenze dedicate a un bisogno o una fase specifica della crescita."}
                    {svc.key === "visita-domicilio" && "Visita o affiancamento educativo personalizzato presso la famiglia."}
                    {svc.key === "formazione-tate" && "Formazione o accompagnamento dedicato alle tate."}
                    {svc.key === "voucher-valore" && "Voucher a valore da concordare direttamente con Teresa."}
                  </p>
                  <div className="mt-5 space-y-2">
                    <a
                      href={wa}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`voucher-whatsapp-${svc.key}`}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#25D366] hover:bg-[#1ea851] text-white text-xs font-semibold uppercase tracking-wider transition-colors"
                    >
                      Richiedi info su WhatsApp
                    </a>
                    <button
                      onClick={() => pickService(svc)}
                      data-testid={`voucher-pick-${svc.key}`}
                      className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border-2 text-xs font-semibold uppercase tracking-wider transition-colors ${selected ? "bg-[#FF2768] border-[#FF2768] text-white" : "border-[#3D2140]/20 text-[#3D2140] hover:border-[#FF2768] hover:text-[#FF2768]"}`}
                    >
                      {selected ? "✓ Selezionato" : "Crea voucher online"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="mt-6 text-center text-xs text-[#3D2140]/60 italic">La richiesta su WhatsApp non è una prenotazione o un acquisto automatico — ne parliamo insieme per trovare la soluzione più adatta.</p>
        </div>
      </section>

      {/* COME FUNZIONA */}
      <section id="come-funziona" className="px-6 md:px-10 py-16 md:py-20">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] font-bold">— Come funziona</p>
          <h2 className="mt-3 font-display italic text-4xl md:text-5xl text-[#3D2140]">In quattro passi semplici</h2>
        </div>

        <div className="max-w-5xl mx-auto mt-14 grid grid-cols-1 md:grid-cols-4 gap-5">
          {[
            { n: "01", t: "Compili il form", d: "Scegli il voucher, l'occasione, indichi a chi va e aggiungi un messaggio." },
            { n: "02", t: "Ti contatto io", d: "Ricevo la richiesta e ti scrivo con il prezzo e le coordinate di pagamento." },
            { n: "03", t: "Paghi come preferisci", d: "Bonifico, contanti, PayPal — come ti torna più comodo." },
            { n: "04", t: "Consegni il regalo", d: "PDF via email o lo stampi e lo dai a mano. Vale un anno intero." },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-2xl p-6 border border-[#3D2140]/8">
              <p className="font-display italic text-5xl text-[#FFE45C]">{s.n}</p>
              <h3 className="mt-3 font-display text-lg text-[#3D2140]">{s.t}</h3>
              <p className="mt-2 text-sm text-[#3D2140]/65 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FORM + LIVE PREVIEW */}
      <section id="voucher-form" className="px-6 md:px-10 py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFE45C]/60 mb-5">
              <Sparkles size={14} className="text-[#3D2140]" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#3D2140] font-bold">Richiesta voucher</span>
            </div>
            <h2 className="font-display italic text-4xl md:text-5xl text-[#3D2140]">Prepara il tuo regalo</h2>
            <p className="mt-4 text-[#3D2140]/70 max-w-xl mx-auto">Compila con calma. Ti risponderò io personalmente entro 24 ore.</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* FORM */}
            <form onSubmit={submit} className="bg-[#FAF8F5] rounded-3xl p-6 md:p-8 space-y-5 border border-[#3D2140]/8" data-testid="voucher-form">
              {/* Servizio + occasione */}
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#3D2140]/60 font-bold mb-2">Pacchetto *</label>
                  <select value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })} className="w-full px-4 py-3 rounded-xl bg-white border border-[#3D2140]/10 text-sm focus:border-[#FF2768] outline-none" required data-testid="input-service-type">
                    <option value="">Scegli il voucher…</option>
                    {services.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#3D2140]/60 font-bold mb-2">Occasione *</label>
                  <select value={form.occasion} onChange={(e) => setForm({ ...form, occasion: e.target.value })} className="w-full px-4 py-3 rounded-xl bg-white border border-[#3D2140]/10 text-sm focus:border-[#FF2768] outline-none" data-testid="input-occasion">
                    {occasions.map((o) => <option key={o.key} value={o.key}>{OCCASION_LABELS[o.key] || o.label}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-4 border-t border-[#3D2140]/8">
                <div className="space-y-3">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF2768] font-bold">Da parte di</p>
                  <input value={form.buyer_name} onChange={(e) => setForm({ ...form, buyer_name: e.target.value })} placeholder="Il tuo nome *" className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#3D2140]/10 text-sm focus:border-[#FF2768] outline-none" required data-testid="input-buyer-name" />
                  <input type="email" value={form.buyer_email} onChange={(e) => setForm({ ...form, buyer_email: e.target.value })} placeholder="La tua email *" className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#3D2140]/10 text-sm focus:border-[#FF2768] outline-none" required data-testid="input-buyer-email" />
                  <input value={form.buyer_phone} onChange={(e) => setForm({ ...form, buyer_phone: e.target.value })} placeholder="Telefono / WhatsApp" className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#3D2140]/10 text-sm focus:border-[#FF2768] outline-none" data-testid="input-buyer-phone" />
                </div>

                <div className="space-y-3">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#4E9EA5] font-bold">Per</p>
                  <input value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} placeholder="Nome destinatario *" className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#3D2140]/10 text-sm focus:border-[#FF2768] outline-none" required data-testid="input-recipient-name" />
                  <input type="email" value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} placeholder="Email destinatario (opzionale)" className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#3D2140]/10 text-sm focus:border-[#FF2768] outline-none" data-testid="input-recipient-email" />
                  <select value={form.delivery} onChange={(e) => setForm({ ...form, delivery: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#3D2140]/10 text-sm focus:border-[#FF2768] outline-none" data-testid="input-delivery">
                    <option value="digital">💌 Digitalmente (PDF via email)</option>
                    <option value="physical">🎁 A mano (lo stampo io)</option>
                    <option value="both">💌🎁 Entrambi</option>
                  </select>
                </div>
              </div>

              <div className="pt-4 border-t border-[#3D2140]/8">
                <label className="block text-xs text-[#3D2140]/70 mb-1.5">Messaggio personale <span className="text-[10px] text-[#3D2140]/40">(apparirà sul voucher)</span></label>
                <textarea value={form.personal_message} onChange={(e) => setForm({ ...form, personal_message: e.target.value })} rows={3} maxLength={500} placeholder="es. Buon compleanno! Che sia un piccolo aiuto quando ne sentirai il bisogno." className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#3D2140]/10 text-sm focus:border-[#FF2768] outline-none resize-none" data-testid="input-personal-message" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-[#3D2140]/70 mb-1.5">Come preferisci pagare?</label>
                  <select value={form.payment_preference} onChange={(e) => setForm({ ...form, payment_preference: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#3D2140]/10 text-sm focus:border-[#FF2768] outline-none" data-testid="input-payment-preference">
                    {Object.entries(PAYMENT_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-[#3D2140]/70 mb-1.5">Codice amico <span className="text-[10px] text-[#3D2140]/40">(-10%, se ne hai uno)</span></label>
                  <div className="flex gap-2">
                    <input value={form.discount_code} onChange={(e) => setForm({ ...form, discount_code: e.target.value.toUpperCase() })} onBlur={validateDiscount} placeholder="AMICA-XXXX" className={`flex-1 px-4 py-2.5 rounded-xl bg-white border text-sm outline-none font-mono ${discountStatus?.valid ? "border-[#10B981]" : discountStatus && discountStatus.valid === false ? "border-red-400" : "border-[#3D2140]/10 focus:border-[#FF2768]"}`} data-testid="input-discount-code" />
                    {discountStatus?.valid && <span className="inline-flex items-center px-3 rounded-xl bg-[#10B981] text-white text-xs font-bold">-10%</span>}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs text-[#3D2140]/70 mb-1.5">Note aggiuntive per me <span className="text-[10px] text-[#3D2140]/40">(opzionale)</span></label>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} maxLength={500} className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#3D2140]/10 text-sm focus:border-[#FF2768] outline-none resize-none" data-testid="input-notes" />
              </div>

              <label className="flex items-start gap-3 cursor-pointer pt-2">
                <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-1 w-4 h-4 accent-[#FF2768]" required data-testid="input-consent" />
                <span className="text-xs text-[#3D2140]/75 leading-relaxed">Confermo di aver letto la <Link to="/privacy" className="underline text-[#FF2768]">privacy policy</Link> e acconsento al trattamento dei dati per gestire questa richiesta.</span>
              </label>

              <button type="submit" disabled={!canSubmit() || sending} className="w-full py-4 rounded-full bg-[#FF2768] text-white font-bold uppercase tracking-widest text-sm hover:bg-[#e91e5e] disabled:opacity-40 disabled:cursor-not-allowed transition-all" data-testid="btn-submit-voucher">
                {sending ? "Sto preparando il voucher..." : "🎁 Prepara il voucher"}
              </button>
              <p className="text-center text-xs text-[#3D2140]/50">
                Preferisci parlarne prima?{" "}
                <a href={BRAND.whatsappUrl} target="_blank" rel="noopener noreferrer" className="text-[#FF2768] underline">Scrivimi su WhatsApp</a>
              </p>
              <div className="pt-4 border-t border-[#3D2140]/8">
                <Link to="/regala-cura-aziende" className="flex items-center gap-3 p-3 rounded-xl bg-[#3D2140] text-white hover:bg-[#164244]" data-testid="link-aziende">
                  <span className="text-lg">🏢</span>
                  <div className="flex-1">
                    <p className="text-xs font-bold uppercase tracking-widest text-[#FFE45C]">Per le aziende</p>
                    <p className="text-sm">Welfare aziendale per neogenitori →</p>
                  </div>
                </Link>
              </div>
            </form>

            {/* LIVE PREVIEW */}
            <div className="lg:sticky lg:top-6 self-start">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#4E9EA5] font-bold mb-3 text-center">Anteprima del voucher</p>
              <div className="bg-gradient-to-br from-[#FDECEF] to-[#FFF5F7] rounded-3xl p-6 border-2 border-dashed border-[#FF2768]/20" data-testid="voucher-preview">
                <div className="bg-white rounded-2xl overflow-hidden shadow-2xl aspect-[3/4] flex flex-col">
                  {/* Header rosa */}
                  <div className="bg-[#FF2768] text-white px-5 pt-5 pb-4">
                    <p className="text-[8px] uppercase tracking-[0.25em] font-bold opacity-90">Teresa Vilardo · Crescere con Cura</p>
                    <p className="font-display italic text-4xl leading-none mt-2">Un regalo</p>
                    <p className="font-display italic text-4xl leading-none">di cura</p>
                  </div>
                  {/* Yellow ribbon */}
                  <div className="bg-[#FFE45C] px-5 py-2">
                    <p className="text-[10px] font-bold text-[#3D2140] text-center uppercase tracking-wider truncate">
                      {currentService?.label || "— Scegli il pacchetto —"}
                    </p>
                  </div>
                  {/* Body */}
                  <div className="flex-1 px-5 py-4 flex flex-col">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-[8px] font-bold text-[#4E9EA5] uppercase tracking-widest">Da</p>
                        <p className="text-sm text-[#3D2140] mt-0.5 truncate">{form.buyer_name || "il tuo nome"}</p>
                      </div>
                      <div>
                        <p className="text-[8px] font-bold text-[#4E9EA5] uppercase tracking-widest">A</p>
                        <p className="text-sm text-[#3D2140] mt-0.5 truncate">{form.recipient_name || "chi lo riceve"}</p>
                      </div>
                    </div>

                    <div className="mt-3">
                      <p className="text-[8px] font-bold text-[#4E9EA5] uppercase tracking-widest">Occasione</p>
                      <p className="text-xs text-[#3D2140] mt-0.5">{OCCASION_LABELS[form.occasion] || "—"}</p>
                    </div>

                    {form.personal_message && (
                      <div className="mt-3">
                        <p className="text-[8px] font-bold text-[#4E9EA5] uppercase tracking-widest">Messaggio</p>
                        <p className="text-xs italic text-[#3D2140]/80 mt-0.5 line-clamp-3">"{form.personal_message}"</p>
                      </div>
                    )}

                    <div className="mt-auto pt-3 border-t border-[#3D2140]/10">
                      <div className="flex items-end justify-between">
                        <div>
                          <p className="text-[8px] font-bold text-[#4E9EA5] uppercase tracking-widest">Valore</p>
                          <p className="text-sm italic text-[#3D2140]/70 mt-0.5">concordato con Teresa</p>
                        </div>
                        <div className="text-right">
                          <p className="text-[8px] font-bold text-[#4E9EA5] uppercase tracking-widest">Codice</p>
                          <p className="font-mono text-xs text-[#3D2140] mt-0.5 opacity-40">CURA-••••-••••</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Footer */}
                  <div className="border-t border-[#3D2140]/10 px-5 py-2">
                    <p className="text-[8px] text-[#3D2140]/60 text-center">www.teresavilardo.it · WhatsApp 340 399 3522</p>
                  </div>
                </div>
                <p className="text-center text-[10px] text-[#3D2140]/50 italic mt-4">L'anteprima si aggiorna mentre compili 💗</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
